import { FormFieldConfig } from '@shared/components/search-form/search-form.component';
import { TableColumn } from '@shared/components/data-table/data-table.component';

// ============================================================================
// TABLE COLUMN CONFIGURATION
// ============================================================================

/**
 * Default column property names (shown on first load)
 * Update these constants to change column property names
 */
export const INTERNAL_RETURN_DEFAULT_COLUMNS = {
  INTERNAL_RETURN_NO: 'internalReturnNo',
  INTERNAL_ORDER_NO: 'internalOrderNo',
  BUYER_NICKNAME: 'buyerNickname',
  SALES_STORE: 'salesStore',
  STATUS: 'status',
  RETURN_REFUND_REASON: 'returnRefundReason',
  RETURN_QTY: 'returnQty',
  BUYER_REFUND_TOTAL: 'buyerRefundTotal',
  INTERNAL_CREATED_AT: 'creationDate',
} as const;

/**
 * Flexible column property names (can be shown/hidden via column selector)
 * Update these constants to change flexible column property names
 */
export const INTERNAL_RETURN_FLEXIBLE_COLUMNS = {
  RETURN_TYPE: 'returnType',
  SETTLEMENT_METHOD: 'settlementMethod',
  RECEIVING_WAREHOUSE: 'receivingWarehouse',
  THIRD_RETURN_NO: 'thirdReturnNo',
  RETURN_ORDER_TYPE: 'returnOrderType',
  THIRD_ORDER_NO: 'thirdOrderNo',
  RETURN_SOURCE: 'returnOrderSource',
  BUYER_NOTES: 'buyerNotes',
  CS_NOTES: 'csNotes',
  RETURN_LOGISTICS_INFO: 'returnLogisticsInfo',
  RETURNER: 'returnerName',
  RETURNER_PHONE: 'returnerPhone',
  RETURNER_ADDRESS: 'returnerAddress',
  SALES_CHANNEL: 'salesChannel',
  THIRD_RETURN_TIME: 'thirdReturnTime',
  RETURN_COMPLETED_AT: 'returnCompletedTime',
} as const;

/**
 * Action column property name
 */
export const INTERNAL_RETURN_ACTION_COLUMN = {
  ACTIONS: 'actions',
} as const;

/**
 * Default columns order (shown on first load)
 * Update this array to change the default column order
 */
export const INTERNAL_RETURN_DEFAULT_COLUMNS_ORDER: string[] = [
  INTERNAL_RETURN_DEFAULT_COLUMNS.INTERNAL_RETURN_NO,
  INTERNAL_RETURN_DEFAULT_COLUMNS.INTERNAL_ORDER_NO,
  INTERNAL_RETURN_DEFAULT_COLUMNS.SALES_STORE,
  INTERNAL_RETURN_DEFAULT_COLUMNS.STATUS,
  INTERNAL_RETURN_DEFAULT_COLUMNS.BUYER_NICKNAME,
  INTERNAL_RETURN_DEFAULT_COLUMNS.RETURN_REFUND_REASON,
  INTERNAL_RETURN_DEFAULT_COLUMNS.RETURN_QTY,
  INTERNAL_RETURN_DEFAULT_COLUMNS.BUYER_REFUND_TOTAL,
  INTERNAL_RETURN_DEFAULT_COLUMNS.INTERNAL_CREATED_AT,
];

/**
 * Flexible columns array (can be shown/hidden via column selector)
 * Update this array to change which columns are flexible
 */
export const INTERNAL_RETURN_FLEXIBLE_COLUMNS_ORDER: string[] = [
  INTERNAL_RETURN_FLEXIBLE_COLUMNS.RETURN_TYPE,
  INTERNAL_RETURN_FLEXIBLE_COLUMNS.SETTLEMENT_METHOD,
  INTERNAL_RETURN_FLEXIBLE_COLUMNS.RECEIVING_WAREHOUSE,
  INTERNAL_RETURN_FLEXIBLE_COLUMNS.THIRD_RETURN_NO,
  INTERNAL_RETURN_FLEXIBLE_COLUMNS.RETURN_ORDER_TYPE,
  INTERNAL_RETURN_FLEXIBLE_COLUMNS.THIRD_ORDER_NO,
  INTERNAL_RETURN_FLEXIBLE_COLUMNS.RETURN_SOURCE,
  INTERNAL_RETURN_FLEXIBLE_COLUMNS.BUYER_NOTES,
  INTERNAL_RETURN_FLEXIBLE_COLUMNS.CS_NOTES,
  INTERNAL_RETURN_FLEXIBLE_COLUMNS.RETURN_LOGISTICS_INFO,
  INTERNAL_RETURN_FLEXIBLE_COLUMNS.RETURNER,
  INTERNAL_RETURN_FLEXIBLE_COLUMNS.RETURNER_PHONE,
  INTERNAL_RETURN_FLEXIBLE_COLUMNS.RETURNER_ADDRESS,
  INTERNAL_RETURN_FLEXIBLE_COLUMNS.SALES_CHANNEL,
  INTERNAL_RETURN_FLEXIBLE_COLUMNS.THIRD_RETURN_TIME,
  INTERNAL_RETURN_FLEXIBLE_COLUMNS.RETURN_COMPLETED_AT,
];

export const INTERNAL_RETURN_TABLE_COLUMNS: TableColumn[] = [
  // Default columns
  {
    label: 'after.internal.return.table.internal_return_no',
    prop: INTERNAL_RETURN_DEFAULT_COLUMNS.INTERNAL_RETURN_NO,
    width: 230,
  },
  {
    label: 'after.internal.return.table.internal_order_no',
    prop: INTERNAL_RETURN_DEFAULT_COLUMNS.INTERNAL_ORDER_NO,
    width: 160,
  },
  {
    label: 'after.internal.return.table.status',
    prop: INTERNAL_RETURN_DEFAULT_COLUMNS.STATUS,
    width: 120,
    type: 'status',
  },
  {
    label: 'after.internal.return.table.buyer_nickname',
    prop: INTERNAL_RETURN_DEFAULT_COLUMNS.BUYER_NICKNAME,
    width: 140,
  },
  {
    label: 'after.internal.return.table.return_refund_reason',
    prop: INTERNAL_RETURN_DEFAULT_COLUMNS.RETURN_REFUND_REASON,
    width: 180,
  },
  {
    label: 'after.internal.return.table.return_qty',
    prop: INTERNAL_RETURN_DEFAULT_COLUMNS.RETURN_QTY,
    width: 80,
  },
  {
    label: 'after.internal.return.table.buyer_refund_total',
    prop: INTERNAL_RETURN_DEFAULT_COLUMNS.BUYER_REFUND_TOTAL,
    width: 160,
    type: 'currency',
  },
  {
    label: 'after.internal.return.table.internal_created_at',
    prop: INTERNAL_RETURN_DEFAULT_COLUMNS.INTERNAL_CREATED_AT,
    width: 150,
    type: 'custom',
    sortable: true,
  },
  // Flexible columns
  {
    label: 'after.internal.return.table.return_type',
    prop: INTERNAL_RETURN_FLEXIBLE_COLUMNS.RETURN_TYPE,
    width: 140,
  },
  {
    label: 'after.internal.return.table.settlement_method',
    prop: INTERNAL_RETURN_FLEXIBLE_COLUMNS.SETTLEMENT_METHOD,
    width: 140,
  },
  {
    label: 'after.internal.return.table.receiving_warehouse',
    prop: INTERNAL_RETURN_FLEXIBLE_COLUMNS.RECEIVING_WAREHOUSE,
    width: 160,
  },
  {
    label: 'after.internal.return.table.third_return_no',
    prop: INTERNAL_RETURN_FLEXIBLE_COLUMNS.THIRD_RETURN_NO,
    width: 160,
  },
  {
    label: 'after.internal.return.table.return_order_type',
    prop: INTERNAL_RETURN_FLEXIBLE_COLUMNS.RETURN_ORDER_TYPE,
    width: 140,
  },
  {
    label: 'after.internal.return.table.third_order_no',
    prop: INTERNAL_RETURN_FLEXIBLE_COLUMNS.THIRD_ORDER_NO,
    width: 160,
  },
  {
    label: 'after.internal.return.table.return_source',
    prop: INTERNAL_RETURN_FLEXIBLE_COLUMNS.RETURN_SOURCE,
    width: 200,
  },
  {
    label: 'after.internal.return.table.buyer_notes',
    prop: INTERNAL_RETURN_FLEXIBLE_COLUMNS.BUYER_NOTES,
    width: 160,
  },
  {
    label: 'after.internal.return.table.cs_notes',
    prop: INTERNAL_RETURN_FLEXIBLE_COLUMNS.CS_NOTES,
    width: 160,
  },
  {
    label: 'after.internal.return.table.return_logistics_info',
    prop: INTERNAL_RETURN_FLEXIBLE_COLUMNS.RETURN_LOGISTICS_INFO,
    width: 180,
  },
  {
    label: 'after.internal.return.table.returner',
    prop: INTERNAL_RETURN_FLEXIBLE_COLUMNS.RETURNER,
    width: 200,
  },
  {
    label: 'after.internal.return.table.returner_phone',
    prop: INTERNAL_RETURN_FLEXIBLE_COLUMNS.RETURNER_PHONE,
    width: 160,
  },
  {
    label: 'after.internal.return.table.returner_address',
    prop: INTERNAL_RETURN_FLEXIBLE_COLUMNS.RETURNER_ADDRESS,
    width: 220,
  },
  {
    label: 'after.internal.return.table.sales_store',
    prop: INTERNAL_RETURN_DEFAULT_COLUMNS.SALES_STORE,
    width: 160,
  },
  {
    label: 'after.internal.return.table.sales_channel',
    prop: INTERNAL_RETURN_FLEXIBLE_COLUMNS.SALES_CHANNEL,
    width: 140,
  },
  {
    label: 'after.internal.return.table.third_return_time',
    prop: INTERNAL_RETURN_FLEXIBLE_COLUMNS.THIRD_RETURN_TIME,
    width: 180,
  },
  {
    label: 'after.internal.return.table.return_completed_at',
    prop: INTERNAL_RETURN_FLEXIBLE_COLUMNS.RETURN_COMPLETED_AT,
    width: 180,
  },
  // Action column
  {
    label: 'common.table.columns.actions',
    prop: INTERNAL_RETURN_ACTION_COLUMN.ACTIONS,
    type: 'actions',
    width: 100,
    sortable: false,
  },
];

// ============================================================================
// SEARCH FORM CONFIGURATION
// ============================================================================

export const INTERNAL_RETURN_SEARCH_CONFIG: FormFieldConfig[] = [
  {
    key: 'orderNumber',
    label: 'after.internal.return.search.order_number',
    type: 'text',
    placeholder: 'after.internal.return.search.order_number_placeholder',
    width: '180px',
  },
  {
    key: 'salesStore',
    label: 'after.internal.return.search.sales_store',
    type: 'multiselect',
    options: [],
    placeholder: 'after.internal.return.search.sales_store_placeholder',
    filter: true,
    showSelectAll: true,
    width: '250px',
  },
  {
    key: 'status',
    label: 'after.internal.return.search.status',
    type: 'select',
    options: [
      { label: 'after.internal.return.status.wait_audit', value: '0' },
      { label: 'after.internal.return.status.wait_refund', value: '3' },
      { label: 'after.internal.return.status.completed', value: '4' },
      { label: 'after.internal.return.status.rejected', value: '5' },
      { label: 'after.internal.return.status.refund_failed', value: '8' },
    ],
    placeholder: 'after.internal.return.search.status_placeholder',
  },
  {
    key: 'buyerNickname',
    label: 'after.internal.return.search.buyer_nickname',
    type: 'text',
    placeholder: 'after.internal.return.search.buyer_nickname_placeholder',
  },
  {
    key: 'creationDate',
    label: 'after.internal.return.search.creation_date',
    type: 'dateRange',
    range: { startKey: 'beginCreationDate', endKey: 'endCreationDate' },
  },
];

// ============================================================================
// CONSTANTS
// ============================================================================

export const INTERNAL_RETURN_DEFAULT_CONFIG = {
  PAGE_SIZE: 20,
  STORE_LOAD_PAGE_SIZE: 100,
  DEFAULT_SORT_BY: 'creationDate',
  DEFAULT_SORT_ASCENDING: false,
} as const;

// Return type mapping
export const RETURN_TYPE_MAP: Record<string, string> = {
  '1': 'Refund Only',
  '2': 'Return & Refund',
} as const;

// Status constants
export const INTERNAL_RETURN_STATUS = {
  WAIT_AUDIT: 'WAIT_AUDIT',
  WAIT_REFUND: 'WAIT_REFUND',
  COMPLETE: 'COMPLETE',
  REFUSE: 'REFUSE',
  REFUND_FAILED: 'REFUND_FAILED',
} as const;

// Status mapping: number -> constant
export const STATUS_MAP: Record<string, string> = {
  '0': INTERNAL_RETURN_STATUS.WAIT_AUDIT,
  '3': INTERNAL_RETURN_STATUS.WAIT_REFUND,
  '4': INTERNAL_RETURN_STATUS.COMPLETE,
  '5': INTERNAL_RETURN_STATUS.REFUSE,
  '8': INTERNAL_RETURN_STATUS.REFUND_FAILED,
} as const;
